import { enEspanol } from "@shared/pbErrors";
import PocketBase, { LocalAuthStore } from "pocketbase";

/** Sesión del panel (quien construye). */
export const pb = new PocketBase("/pb", new LocalAuthStore("plane_builder"));

/** Sesión de las apps publicadas (quien las usa). */
export const pbApp = new PocketBase("/pb", new LocalAuthStore("plane_member"));

pb.autoCancellation(false);
pbApp.autoCancellation(false);

export class ApiError extends Error {
  constructor(
    message: string,
    /** El `codigo` de la respuesta, cuando el servidor distingue el error con uno. */
    public code?: string,
  ) {
    super(message);
  }
}

/**
 * El cuerpo de una respuesta, cuando de verdad viene en JSON.
 *
 * No siempre viene: un proxy que se cae, el servidor de desarrollo apagado o
 * una página de error del navegador llegan en HTML, y ahí `JSON.parse` lanzaba
 * un `SyntaxError: Unexpected token '<'` que subia tal cual hasta la pantalla.
 * Lo que se le enseña a quien construye tiene que ser una frase suya, así que
 * lo que no se entiende cuenta como que no vino nada.
 */
export function jsonBody(text: string): { error?: string; codigo?: string } | null {
  if (!text.trim()) return null;
  try {
    return JSON.parse(text) as { error?: string; codigo?: string };
  } catch {
    return null;
  }
}

/** El mensaje de una respuesta que falló, siempre legible. */
export const httpError = (
  res: Response,
  data: { error?: string; codigo?: string } | null,
): ApiError => new ApiError(data?.error ?? `El servidor respondio ${res.status}`, data?.codigo);

/** Llama a la API propia de la plataforma con la sesión del panel. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (pb.authStore.token) headers.set("authorization", pb.authStore.token);
  // Un formulario pone su propio tipo, con la frontera dentro: escribirlo aquí
  // dejaría el cuerpo ilegible. Todo lo demás es JSON.
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("content-type", "application/json");
  }

  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  const data = jsonBody(text);

  if (!res.ok) throw httpError(res, data);
  return data as T;
}

export const post = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

export const patch = <T>(path: string, body: unknown) =>
  api<T>(path, { method: "PATCH", body: JSON.stringify(body) });

export const put = <T>(path: string, body: unknown) =>
  api<T>(path, { method: "PUT", body: JSON.stringify(body) });

export const del = <T>(path: string) => api<T>(path, { method: "DELETE" });

/**
 * Una petición que llega por partes, a medida que el servidor la produce.
 *
 * Se usa donde esperar al final sería esperar a ciegas. Cada parte se entrega
 * en cuanto llega; el resultado de verdad viaja dentro de una de ellas, y de
 * eso ya sabe quien llama.
 */
export async function stream<T>(
  path: string,
  body: unknown,
  onPart: (part: T) => void,
  /** Para cortar un hilo que no termina solo, como el del modo MCP. */
  signal?: AbortSignal,
): Promise<void> {
  const headers = new Headers({ "content-type": "application/json" });
  if (pb.authStore.token) headers.set("authorization", pb.authStore.token);

  const res = await fetch(path, { method: "POST", headers, body: JSON.stringify(body), signal });
  if (!res.ok || !res.body) {
    throw httpError(res, jsonBody(await res.text()));
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;

      // Cada parte termina en línea en blanco; un JSON incompleto espera al siguiente trozo.
      let cut = buffer.indexOf("\n\n");
      while (cut !== -1) {
        const chunk = buffer.slice(0, cut).trim();
        buffer = buffer.slice(cut + 2);
        if (chunk.startsWith("data:")) {
          let part: T;
          try {
            part = JSON.parse(chunk.slice(5).trim()) as T;
          } catch {
            // Solo se descarta el JSON ilegible, no los errores de quien procesa el evento.
            cut = buffer.indexOf("\n\n");
            continue;
          }
          onPart(part);
        }
        cut = buffer.indexOf("\n\n");
      }
    }
  } finally {
    // Si falla el procesamiento, el servidor no debe seguir enviando a un lector abandonado.
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

/** Lo que una respuesta fallida dice que pasó, recogido tal como viene. */
interface ApiFault {
  /** Las columnas rechazadas: la única parte que se lee sin saber de la base. */
  fields: { field: string; message: string; code?: string }[];
  /** El mensaje de cada nivel, de fuera adentro y sin repetirse. */
  trail: string[];
  /** El estado HTTP más hondo que aparezca. */
  status?: number;
}

/**
 * Lee una respuesta fallida de PocketBase sin tirar nada.
 *
 * La API de lote anida: el nivel de fuera solo dice "Batch transaction failed",
 * que no nombra ni la columna ni el motivo, y lo que de verdad pasó vive en
 * `requests.N.response`. De ahí la recursión. Se guardan los tres niveles
 * --columnas, mensajes y estado-- porque un fallo que nadie previo no tiene por
 * que caber en el primero, y lo que no se recoge aquí no se puede enseñar.
 */
function readFault(response: unknown, fault: ApiFault, depth = 0): void {
  if (!response || typeof response !== "object" || depth > 4) return;
  const level = response as { message?: unknown; status?: unknown; data?: unknown };

  if (typeof level.message === "string" && level.message.trim()) {
    const text = level.message.trim();
    if (!fault.trail.includes(text)) fault.trail.push(text);
  }
  if (typeof level.status === "number") fault.status = level.status;
  if (!level.data || typeof level.data !== "object") return;

  for (const [key, value] of Object.entries(level.data as Record<string, unknown>)) {
    if (!value || typeof value !== "object") continue;

    // `requests` no es una columna: es la lista de pedidos del lote, y cada uno
    // trae dentro su propia respuesta, que es la que sabe que falló.
    if (key === "requests") {
      for (const request of Object.values(value as Record<string, unknown>)) {
        const inner = request as { response?: unknown } | null;
        readFault(inner?.response, fault, depth + 1);
      }
      continue;
    }

    const entry = value as { code?: unknown; message?: unknown; response?: unknown };
    if (entry.response) {
      readFault(entry.response, fault, depth + 1);
      continue;
    }
    if (typeof entry.message === "string" && entry.message.trim()) {
      fault.fields.push({
        field: key,
        message: entry.message.trim(),
        code: typeof entry.code === "string" ? entry.code : undefined,
      });
    }
  }
}

/** Lo que cabe en el renglón de detalle antes de cortarlo. */
const DETAIL_MAX = 300;

/** Recorta un texto al máximo de detalle que se muestra en un error. */
const clamp = (text: string) =>
  text.length > DETAIL_MAX ? `${text.slice(0, DETAIL_MAX - 1)}\u2026` : text;

/** Termina la frase en punto, salvo que ya acabe en signo propio. */
const sentence = (text: string) => (/[.!?…:]$/.test(text) ? text : `${text}.`);

/**
 * Lo que sigue a los dos puntos va en minúscula, como se escribe en español.
 * Una sigla o un nombre propio (`JSON`, `PocketBase`) se deja como viene: se
 * reconoce porque su segunda letra también es mayúscula.
 */
const afterColon = (text: string) =>
  /^\p{Lu}\p{Ll}/u.test(text) ? text[0].toLowerCase() + text.slice(1) : text;

/** `fecha_nacimiento` → `Fecha nacimiento`, para cuando no se sabe la etiqueta. */
const humanize = (name: string) => {
  const words = name.replace(/[_-]+/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : name;
};

/**
 * Mensaje legible para cualquier error que llegue del servidor.
 *
 * Dos renglones: arriba lo que hay que arreglar --la columna y lo que se le
 * objeta-- y debajo lo que dijo la API para llegar hasta ahí.
 *
 * Cuando todo lo que dijo la base está traducido, el aviso se queda en esas
 * frases: "validation_is_email · HTTP 400" no le dice nada a quien construye.
 * El código y el estado solo salen cuando algo llegó sin traducir --un fallo
 * que nadie previo--, que es justo cuando hacen falta para mirarlo y copiarlo.
 *
 * `fields` son las columnas de la tabla, si se conocen: con ellas el aviso
 * nombra la columna por su etiqueta («Correo electrónico») y no por su nombre
 * técnico (`correo_electronico`).
 *
 * Un error de la API propia de la plataforma no pasa por aquí: ya llega escrito
 * en una frase suya (ver `httpError`).
 */
export function errorMessage(
  err: unknown,
  fields?: readonly { name: string; label?: string }[],
): string {
  if (err instanceof ApiError) return err.message;
  if (!err || typeof err !== "object") return "No se pudo completar la acción. Inténtalo de nuevo.";

  const e = err as { message?: string; response?: unknown };
  const fault: ApiFault = { fields: [], trail: [] };
  readFault(e.response, fault);

  /*
   * La base contesta en inglés y aquí ya se dice en español; lo que no esté
   * traducido pasa tal cual (ver `shared/pbErrors`) y se recuerda, porque eso
   * es lo que decide si el detalle técnico sale o no.
   *
   * Lo repetido se quita otra vez porque dos frases suyas distintas pueden
   * acabar en la misma nuestra.
   */
  let untranslated = false;
  const translate = (message: string, code?: string) => {
    const said = enEspanol(message, code);
    if (said === message.trim()) untranslated = true;
    return said;
  };
  const columns = fault.fields.map((f) => ({
    ...f,
    label: fields?.find((c) => c.name === f.field)?.label || humanize(f.field),
    message: translate(f.message, f.code),
  }));
  const trail = [...new Set(fault.trail.map((t) => translate(t)))];

  const head = columns.length
    ? columns.map((f) => sentence(`${f.label}: ${afterColon(f.message)}`)).join(" ")
    : sentence(
        trail[0] ??
          (e.message ? translate(e.message) : undefined) ??
          "No se pudo completar la acción. Inténtalo de nuevo",
      );

  const parts: string[] = [];
  // Si se conoce la columna y el motivo, repetir el fallo de guardado no ayuda.
  const genericFailures = new Set([
    "No se pudo crear la fila",
    "No se pudo guardar la fila",
    "Hay datos incorrectos. Revísalos antes de continuar",
  ]);
  const rest = trail.filter(
    (t) => sentence(t) !== head && !(columns.length && genericFailures.has(t)),
  );
  // Sin traducir, el renglón es para copiarlo: se queda con sus separadores.
  if (rest.length) parts.push(untranslated ? rest.join(" \u2192 ") : rest.map(sentence).join(" "));
  if (untranslated) {
    const codes = [...new Set(columns.map((f) => f.code).filter(Boolean))];
    if (codes.length) parts.push(codes.join(", "));
  }
  /*
   * De la respuesta no salió nada legible --ni columnas ni un mensaje suyo--,
   * así que se enseña su cuerpo tal cual. Es feo a propósito, es JSON, y es lo
   * único que sirve cuando la forma no es ninguna de las previstas: sin esto el
   * aviso se quedaba en "Algo salió mal" y no había nada que mirar ni copiar.
   */
  if (!trail.length && !columns.length && e.response) {
    try {
      const raw = JSON.stringify(e.response);
      if (raw && raw !== "{}" && raw !== head) {
        parts.push(raw);
        untranslated = true;
      }
    } catch {
      /* un cuerpo que no se puede serializar no cuenta como detalle */
    }
  }
  /*
   * El estado acompaña a un fallo sin traducir, no es un fallo por si solo: un
   * "Failed to authenticate." --lo que ve quien se equivoca de clave al entrar
   * en una aplicación publicada-- no tiene por que llevar un HTTP 400 detrás.
   */
  if (untranslated && fault.status && parts.length) parts.push(`HTTP ${fault.status}`);

  return parts.length ? `${head}\n${clamp(parts.join(" \u00b7 "))}` : head;
}

/**
 * Si el fallo fue por pedir demasiado rápido, mire donde mire.
 *
 * Un lote lo dice por dentro: el pedido que se pasó del límite trae su `429` y
 * el lote entero responde `400`, así que el estado de fuera no sirve para
 * reconocerlo. Se busca en todos los niveles, que es lo que permite volver a
 * intentar el tramo en vez de tumbar la importación. Ver
 * `shared/importBatch.ts`.
 */
export function isRateLimited(err: unknown, depth = 0): boolean {
  if (!err || typeof err !== "object" || depth > 6) return false;
  const node = err as Record<string, unknown>;
  if (node.status === 429) return true;
  return Object.values(node).some((value) => isRateLimited(value, depth + 1));
}

/** URL de un archivo guardado en PocketBase. */
export function fileUrl(
  record: { id: string; collectionId?: string; collectionName?: string },
  filename: string,
  thumb?: string,
): string {
  if (!filename) return "";
  const collection = record.collectionId ?? record.collectionName ?? "";
  // Codificado: un nombre de archivo con un `#` o un `?` dentro partiria la
  // dirección y el navegador pediria otra cosa.
  const base = `/pb/api/files/${encodeURIComponent(collection)}/${encodeURIComponent(
    record.id,
  )}/${encodeURIComponent(filename)}`;
  return thumb ? `${base}?thumb=${encodeURIComponent(thumb)}` : base;
}
